<template>
  <div class="line-item flex items-center gap-100">
    <LcLotteryNumbers
      v-bind="{ size, spacing, rows }"
      :fields="preparedFields"
      :style="[...addOnThemes, ...additionalThemes, extraTheme]"
    >
      <template
        v-for="addOn in data?.lowlightAddOns"
        :key="addOn"
        #[`number-${addOn}`]="{ number }"
      >
        {{ getLowlightText(addOn, number) }}
      </template>

      <template v-for="{ name, src } of data?.highlightAddOns" :key="name" #[`highlight-${name}`]>
        <img v-bind="{ src }" :alt="name" class="highlight" />
      </template>

      <template v-if="$slots['field-divider']" #field-divider>
        <slot name="field-divider" />
      </template>
    </LcLotteryNumbers>

    <strong
      v-if="!hidePerPlayAddOn && !isExtraHitEnabled && perPlayAddOn"
      class="per-play-add-on font-regular"
    >
      {{ perPlayAddOnTranslation }}
    </strong>
  </div>
</template>

<script lang="ts" setup>
import type {
  LcIconName,
  LcLotteryNumber,
  LcLotteryNumbersField,
  LcLotteryNumbersProps,
} from '@lottocom/frontend-components'

import {
  type AddOn,
  type AddOnOutput,
  LotteryId,
  type PlaySlipStatus,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { getField } from '#number-lottery/app/utils/fieldMapping'

export type LineItemAddOn = Pick<AddOn, 'name' | 'perPlay'>
export type LineItemField = LcLotteryNumbersField & {
  modifies?: Maybe<string>
  selected?: number[]
}
export type LineItemPerPlayAddOn = Omit<AddOnOutput, '__typename'>

export type LineItemProps = Omit<LcLotteryNumbersProps, 'fields'> & {
  addOns?: LineItemAddOn[]
  fields: LineItemField[]
  hidePerPlayAddOn?: boolean
  highlightAddOn?: boolean
  licensedTerritory: string
  lotteryId: LotteryId
  perPlayAddOn?: Maybe<LineItemPerPlayAddOn>
  state?: PlaySlipStatus
}

const toName = ({ name }: LineItemAddOn) => name.toLowerCase()

const {
  addOns = [],
  fields,
  hidePerPlayAddOn,
  highlightAddOn,
  licensedTerritory,
  lotteryId,
  perPlayAddOn = null,
} = defineProps<LineItemProps>()

const { getTranslation } = useI18nUtils()
const { getPerPlayTranslation } = useTranslations()
const { width } = useWindowSize()

const { t } = useI18n()

const preparedAddOns = computed(() => addOns.map(toName))

const rows = computed(() => {
  const total = fields.reduce((total, field) => (total += field.numbers.length), 0)
  return getCountOfFieldRows(total, lotteryId, width.value)
})

const { data } = await useLineItem(
  () => licensedTerritory,
  () => lotteryId,
  () => highlightAddOn,
  preparedAddOns,
)

const getLowlightText = (addOnName: string, number?: number | string) => {
  if (typeof number === 'undefined') return '-'
  return getTranslation({
    fallback: String(number),
    key: `lineitem.number.addon.${addOnName}`,
    values: { amount: number },
  })
}

const normalizeName = (name: string) => name.toLowerCase().replace('_', '-')

const addOnThemes = computed(() => {
  const { createCssVar } = lotteryCssVarConstructor.value

  return preparedAddOns.value.map((name) => {
    const normalizedName = normalizeName(name)
    return {
      [`--lottery-numbers-${normalizedName}-bg`]: createCssVar(
        `${normalizedName}-bg`,
        'transparent',
      ),
      [`--lottery-numbers-${normalizedName}-border`]: createCssVar(
        `${normalizedName}-border`,
        '0.125rem solid var(--add-on-border-fallback-color)',
      ),
      [`--lottery-numbers-${normalizedName}-color`]: createCssVar(`${normalizedName}-color`),
    }
  })
})
const additionalThemes = computed(() => {
  const { createCssVar } = lotteryCssVarConstructor.value

  return preparedFields.value
    .filter(
      ({ name }) =>
        !name.match(/^([A-Z]{1}|BASE)$/) &&
        !preparedAddOns.value.find((addOn) => name.toLowerCase() === addOn),
    )
    .map(({ name }) => {
      const normalizedName = normalizeName(name)
      return [
        {
          [`--lottery-numbers-${normalizedName}-bg`]: createCssVar(`${normalizedName}-bg`),
          [`--lottery-numbers-${normalizedName}-border`]: createCssVar(`${normalizedName}-border`),
          [`--lottery-numbers-${normalizedName}-color`]: createCssVar(`${normalizedName}-color`),
        },
      ]
    })
})
const extraHitIcon = computed(
  () => `hit-${t(`lottery.${lotteryId}.extrahit`).toLowerCase()}` as LcIconName,
)
const extraTheme = computed(() => {
  const { createCssVar } = lotteryCssVarConstructor.value

  return {
    '--lottery-numbers-extra-bg': createCssVar('extra-ball-bg'),
    '--lottery-numbers-extra-color': createCssVar('extra-ball-color'),
  }
})
const isExtraHitEnabled = computed(() => perPlayAddOn?.name === 'extrahit' && perPlayAddOn?.value)
const lotteryCssVarConstructor = computed(() =>
  getLotteryCssVarConstructor(licensedTerritory, lotteryId),
)
const perPlayAddOnTranslation = computed(() => {
  if (!perPlayAddOn) return null
  return getPerPlayTranslation(lotteryId, perPlayAddOn.name, perPlayAddOn.value)
})
const preparedFields = computed(() =>
  fields.reduce<LineItemField[]>((fields, field) => {
    if (isExtraHitEnabled.value && field.name === 'EXTRA') {
      field.numbers = [{ icon: extraHitIcon.value, value: undefined }]
    }
    if (!field.modifies) return [...fields, fieldValueFormatter(field)]

    const sourceFieldIndex = fields.findIndex(({ name }) => name === field.modifies)

    if (sourceFieldIndex === -1) return fields

    field.numbers
      .map((number) => getModifiedNumberIndexes(fields[sourceFieldIndex], number))
      .filter((indexToBeReplaced) => indexToBeReplaced >= 0)
      .forEach((indexToBeReplaced) => {
        fields = [
          ...fields.slice(0, sourceFieldIndex),
          ...spliceInModifiedField(fields[sourceFieldIndex], field, indexToBeReplaced),
        ]
      })

    return fields
  }, []),
)

/**
 * Apply a custom formatting to a line item field.
 * Currently only needed for the 'year' field for NE MyDay
 */
const fieldValueFormatter = (field: LineItemField) => {
  if (lotteryId === LotteryId.NebraskaMyDay && getField(field.name) === 'year') {
    field.numbers = field.numbers.map((number) => ({
      ...number,
      value:
        typeof number.value === 'number' ? number.value.toString().padStart(2, '0') : number.value,
    }))
  }

  return field
}

const getModifiedNumberIndexes = (sourceField: LineItemField, number: LcLotteryNumber) =>
  sourceField.numbers.findIndex((sourceNumber) => sourceNumber.value === number.value)

const spliceInModifiedField = (
  sourceField: LineItemField,
  modifiedField: LineItemField,
  indexToBeReplaced: number,
) => [
  {
    ...sourceField,
    numbers: sourceField.numbers.slice(0, indexToBeReplaced),
  },
  {
    ...modifiedField,
    numbers: [sourceField.numbers[indexToBeReplaced]],
  },
  {
    ...sourceField,
    numbers: sourceField.numbers.slice(indexToBeReplaced + 1),
  },
]
</script>

<style lang="scss" scoped>
.line-item {
  .lc-lottery-numbers {
    --add-on-border-fallback-color: #{token('color/border/primary/primary')};
  }

  .highlight {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    max-width: unset;
    max-height: 100%;
  }

  .per-play-add-on {
    @include media-mobile-small {
      font-size: token('font/size/xs');
    }
  }
}
</style>
